import React, { useContext, useEffect, useRef, useState } from 'react';

import { app, media } from 'context';
import { useOnClickOutside } from 'ds/helpers';

import Avatar from '../Avatar';
import Clickable from '../Clickable';
import Icon from '../Icon';
import IconButton from '../IconButton';
import Layout from '../Layout';
import PillButton from '../PillButton';
import { StatusPillPaletteId, getStatusPillPalette } from '../StatusPill';
import Text from '../Text';

export interface BulkEditColumnConfig {
  key: string;
  header: string;
  bulkEditStyling?: 'status' | 'avatar' | 'text';
  bulkEditOptions?: { label: string; value: string | number; avatarUrl?: string }[];
  bulkEditValueKey?: string;
  bulkEditGetStatusPillProps?: (value: string | number) => { palette: string; label: string };
  bulkEdit?: (ids: (number | string)[], values: Record<string, unknown>) => Promise<void>;
  /** Optional list of option values to hide from the bulk edit menus (e.g. disallowed statuses). */
  bulkEditHiddenValues?: (string | number)[];
}

interface Props {
  selectedCount: number;
  bulkEditColumns: BulkEditColumnConfig[];
  selectedIds: Set<number | string>;
  selectedRows: { id: number | string; [key: string]: unknown }[];
  onBulkEditComplete: () => void;
  onDismiss: () => void;
}

const FloatingBulkActions: React.FC<Props> = ({
  selectedCount,
  bulkEditColumns,
  selectedIds,
  selectedRows,
  onBulkEditComplete,
  onDismiss
}) => {
  const { isMobile } = useContext(media);
  const { bottomNavigationHeight } = useContext(app);
  const [isVisible, setIsVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuView, setMenuView] = useState<'column-list' | 'value-list' | 'confirm'>('column-list');
  const [selectedColumn, setSelectedColumn] = useState<BulkEditColumnConfig | null>(null);
  const [selectedOptionValue, setSelectedOptionValue] = useState<string | number | null>(null);
  const [hasConfirmedSelection, setHasConfirmedSelection] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [menuWidth, setMenuWidth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      setMenuWidth(containerRef.current.offsetWidth);
    }
  }, [selectedCount, isMobile]);

  const footerHeight = isMobile ? 72 : 60;
  const bottomOffset = (isMobile ? bottomNavigationHeight : 0) + footerHeight + 8;

  const getVisibleOptions = (
    col: BulkEditColumnConfig
  ): { label: string; value: string | number; avatarUrl?: string }[] => {
    const options = col.bulkEditOptions ?? [];
    const hidden = col.bulkEditHiddenValues ?? [];

    if (hidden.length === 0) return options;

    return options.filter(option => !hidden.includes(option.value));
  };

  /** One column and one visible option: Remove applies immediately (no menus). */
  const isSingleDirectBulk = bulkEditColumns.length === 1 && getVisibleOptions(bulkEditColumns[0]).length === 1;

  const bulkPrimaryHoverMuted = (isSingleDirectBulk && isApplying) || (isSingleDirectBulk === false && menuOpen);

  const resetMenuState = () => {
    setMenuOpen(false);
    setMenuView('column-list');
    setSelectedColumn(null);
    setSelectedOptionValue(null);
    setHasConfirmedSelection(false);
  };

  useOnClickOutside(containerRef, () => {
    resetMenuState();
  });

  const handleSingleDirectRemove = async () => {
    if (isApplying) {
      return;
    }
    const col = bulkEditColumns[0];
    const [onlyOption] = getVisibleOptions(col);
    if (col.bulkEdit == null) {
      return;
    }
    setIsApplying(true);
    try {
      const valueKey = col.bulkEditValueKey ?? col.key;
      await col.bulkEdit(Array.from(selectedIds), { [valueKey]: onlyOption.value });
      onBulkEditComplete();
      resetMenuState();
    } finally {
      setIsApplying(false);
    }
  };

  const handlePrimaryBulkClick = () => {
    if (isSingleDirectBulk) {
      void handleSingleDirectRemove();
      return;
    }
    setMenuOpen(!menuOpen);
  };

  const handleColumnClick = (col: BulkEditColumnConfig) => {
    setSelectedColumn(col);
    setSelectedOptionValue(null);
    setHasConfirmedSelection(false);
    setMenuView('value-list');
  };

  const handleValueSelect = (value: string | number) => {
    setSelectedOptionValue(value);
    setHasConfirmedSelection(true);
    setMenuView('confirm');
  };

  const handleValueListBack = () => {
    if (isSingleDirectBulk) {
      resetMenuState();
      return;
    }
    if (hasConfirmedSelection) {
      // User came from the confirm view and is just reconsidering their choice.
      setMenuView('confirm');
      return;
    }
    // First-time selection flow: go back to column selection.
    setSelectedColumn(null);
    setSelectedOptionValue(null);
    setMenuView('column-list');
  };

  const handleConfirmBack = () => {
    if (isSingleDirectBulk) {
      resetMenuState();
      return;
    }
    setSelectedColumn(null);
    setSelectedOptionValue(null);
    setHasConfirmedSelection(false);
    setMenuView('column-list');
  };

  const handleApply = async () => {
    if (selectedColumn == null || selectedOptionValue == null || selectedColumn.bulkEdit == null) return;
    setIsApplying(true);
    try {
      const valueKey = selectedColumn.bulkEditValueKey ?? selectedColumn.key;
      await selectedColumn.bulkEdit(Array.from(selectedIds), { [valueKey]: selectedOptionValue });
      onBulkEditComplete();
      resetMenuState();
    } finally {
      setIsApplying(false);
    }
  };

  if (bulkEditColumns.length === 0) return null;

  return (
    <Layout
      position="fixed"
      left={0}
      right={0}
      bottom={bottomOffset}
      align="center"
      justify="center"
      zIndex={110}
      __style={{
        pointerEvents: 'none'
      }}
    >
      <Layout
        __ref={containerRef}
        position="relative"
        direction="row"
        align="center"
        justify="center"
        gap={12}
        height={64}
        paddingLeft={24}
        paddingRight={12}
        paddingY={8}
        borderRadius={16}
        color="blue-500"
        __style={{
          pointerEvents: 'auto',
          transition: 'transform 150ms ease-out, opacity 150ms ease-out',
          transform: isVisible ? 'translateY(0)' : 'translateY(12px)',
          opacity: isVisible ? 1 : 0,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}
      >
        <Text size="body-sm" color="white">
          {selectedCount} selected
        </Text>
        <Clickable
          onClick={handlePrimaryBulkClick}
          hoverColor={bulkPrimaryHoverMuted ? undefined : 'blue-600'}
          style={{ borderRadius: 1000, marginLeft: 12 }}
          disabled={isSingleDirectBulk && isApplying}
          {...(menuOpen && !isSingleDirectBulk ? { color: 'blue-600' as const } : {})}
        >
          <Layout
            direction="row"
            align="center"
            borderRadius={1000}
            __style={{ borderRadius: 1000 }}
            paddingX={16}
            paddingY={8}
            {...(menuOpen && !isSingleDirectBulk ? { color: 'blue-600' as const } : {})}
          >
            {isSingleDirectBulk ? (
              <>
                <Icon name="trashcan" size="xs" stroke={2.0} color="white" />
                <Layout marginLeft={4}>
                  <Text size="body-sm" color="white" semibold>
                    Remove
                  </Text>
                </Layout>
              </>
            ) : (
              <>
                <Icon name="editPen" size="xs" stroke={2} color="white" />
                <Layout marginLeft={4}>
                  <Text size="body-sm" color="white" semibold>
                    Edit
                  </Text>
                </Layout>
              </>
            )}
          </Layout>
        </Clickable>
        <Layout
          marginLeft={12}
          __style={{
            width: 0,
            alignSelf: 'stretch',
            borderLeft: '1px solid rgba(255, 255, 255, 0.4)'
          }}
        />
        <IconButton
          name="close"
          size="md"
          type="primaryBlue"
          stroke={2}
          onClick={() => {
            resetMenuState();
            setIsVisible(false);
            setTimeout(() => {
              onDismiss();
            }, 150);
          }}
        />
        {menuOpen && (
          <Layout
            position="absolute"
            right={0}
            bottom="calc(100% + 4px)"
            color="white"
            borderRadius={16}
            width={menuWidth ?? undefined}
            minWidth={menuWidth ?? 176}
            __style={{ boxShadow: 'rgba(87, 73, 109, 0.12) 0px 2px 12px 0px' }}
          >
            {menuView === 'column-list' && (
              <Layout paddingY={0}>
                <Layout height={40} align="center" paddingX={16} paddingY={8}>
                  <Text size="body-xs" color="gray-900" semibold>
                    Select column
                  </Text>
                </Layout>
                {bulkEditColumns.map(col => {
                  const styling = col.bulkEditStyling ?? 'text';
                  return (
                    <Clickable key={col.key} onClick={() => handleColumnClick(col)} style={{ width: '100%' }}>
                      <Layout align="center" direction="row" gap={12} hoverColor="gray-50" paddingX={16} paddingY={8}>
                        <Text size="body-sm" color="gray-900">
                          {styling === 'status' ? 'Status' : col.header}
                        </Text>
                      </Layout>
                    </Clickable>
                  );
                })}
              </Layout>
            )}
            {menuView === 'value-list' && selectedColumn && (
              <Layout paddingY={0}>
                <Layout paddingX={4} paddingY={4} direction="row" align="center" gap={4}>
                  <IconButton
                    name="leftChevron"
                    type="noBackground"
                    size="sm"
                    onClick={handleValueListBack}
                    stroke={2}
                  />
                  <Text size="body-xs" color="gray-900" semibold>
                    {`Select new ${selectedColumn.header}`}
                  </Text>
                </Layout>
                <Layout
                  paddingX={0}
                  paddingTop={0}
                  paddingBottom={8}
                  direction="column"
                  gap={0}
                  maxHeight={164}
                  overflowY="auto"
                >
                  {selectedColumn.bulkEditStyling === 'status' && selectedColumn.bulkEditGetStatusPillProps ? (
                    <Layout direction="column" gap={0}>
                      {getVisibleOptions(selectedColumn).map(option => {
                        const statusProps = selectedColumn.bulkEditGetStatusPillProps!(option.value);
                        const palette = getStatusPillPalette(statusProps.palette as StatusPillPaletteId);
                        return (
                          <Clickable
                            key={option.value}
                            onClick={() => handleValueSelect(option.value)}
                            style={{ width: '100%' }}
                          >
                            <Layout
                              direction="row"
                              align="center"
                              gap={12}
                              paddingY={8}
                              paddingX={16}
                              hoverColor="gray-50"
                            >
                              <Layout width={12} height={12} align="center" justify="center">
                                <Layout width={8} height={8} borderRadius="circular" color={palette.dotColor} />
                              </Layout>
                              <Text size="body-sm" color="gray-900">
                                {option.label}
                              </Text>
                            </Layout>
                          </Clickable>
                        );
                      })}
                    </Layout>
                  ) : selectedColumn.bulkEditStyling === 'avatar' ? (
                    <Layout direction="column" gap={0}>
                      {getVisibleOptions(selectedColumn).map(option => (
                        <Clickable
                          key={option.value}
                          onClick={() => handleValueSelect(option.value)}
                          style={{ width: '100%' }}
                        >
                          <Layout
                            direction="row"
                            align="center"
                            gap={12}
                            paddingY={8}
                            paddingX={16}
                            hoverColor="gray-50"
                          >
                            {option.avatarUrl ? <Avatar size={24} avatarUrl={option.avatarUrl} /> : null}
                            <Text size="body-sm" color="gray-900">
                              {option.label}
                            </Text>
                          </Layout>
                        </Clickable>
                      ))}
                    </Layout>
                  ) : (
                    <Layout direction="column" gap={0}>
                      {getVisibleOptions(selectedColumn).map(option => (
                        <Clickable
                          key={option.value}
                          onClick={() => handleValueSelect(option.value)}
                          style={{ width: '100%' }}
                        >
                          <Layout
                            direction="row"
                            align="center"
                            gap={12}
                            paddingY={8}
                            paddingX={16}
                            hoverColor="gray-50"
                          >
                            <Text size="body-sm" color="gray-900">
                              {option.label}
                            </Text>
                          </Layout>
                        </Clickable>
                      ))}
                    </Layout>
                  )}
                </Layout>
              </Layout>
            )}
            {menuView === 'confirm' && selectedColumn && selectedOptionValue != null && (
              <Layout paddingTop={0}>
                <Layout paddingX={4} paddingY={4} direction="row" align="center" gap={4}>
                  <IconButton name="leftChevron" type="noBackground" size="sm" onClick={handleConfirmBack} stroke={2} />
                  <Text size="body-xs" color="gray-900" semibold>
                    Confirm selection
                  </Text>
                </Layout>
                <Layout paddingX={16} paddingY={16} direction="column" gap={12}>
                  {(() => {
                    const option = (selectedColumn.bulkEditOptions ?? []).find(o => o.value === selectedOptionValue);
                    if (!option) return null;

                    const isStatusColumn = selectedColumn.bulkEditStyling === 'status';
                    const isAvatarColumn = selectedColumn.bulkEditStyling === 'avatar';

                    let leading: React.ReactNode = null;

                    if (isStatusColumn && selectedColumn.bulkEditGetStatusPillProps) {
                      const statusProps = selectedColumn.bulkEditGetStatusPillProps(selectedOptionValue);
                      const palette = getStatusPillPalette(statusProps.palette as StatusPillPaletteId);
                      leading = (
                        <Layout width={12} height={12} align="center" justify="center">
                          <Layout width={8} height={8} borderRadius="circular" color={palette.dotColor} />
                        </Layout>
                      );
                    } else if (isAvatarColumn && option.avatarUrl) {
                      leading = <Avatar size={24} avatarUrl={option.avatarUrl} />;
                    }

                    const hasStatusConflict =
                      isStatusColumn &&
                      option.value === 'closed' &&
                      selectedRows.some(row => row[selectedColumn.key] === selectedOptionValue);

                    return (
                      <Clickable onClick={() => setMenuView('value-list')} style={{ width: '100%' }}>
                        <Layout direction="row" align="center" justify="space-between" gap={12}>
                          <Layout direction="row" align="center" gap={12}>
                            {leading}
                            <Text size="body-sm" color="gray-900">
                              {option.label}
                            </Text>
                          </Layout>
                          <Icon name="rightChevron" size="xs" stroke={2} color="gray-600" />
                        </Layout>
                        {hasStatusConflict && (
                          <Layout marginTop={8}>
                            <Text size="body-xs" color="red-700" align="left">
                              {
                                'Closed items cannot be closed again. Remove any Closed items from your selection and try again.'
                              }
                            </Text>
                          </Layout>
                        )}
                      </Clickable>
                    );
                  })()}
                </Layout>
                <Layout paddingX={12} paddingY={12} borderTop borderColor="gray-50">
                  <PillButton
                    type="primary"
                    size="md"
                    text={`Apply to ${selectedCount} items`}
                    disabled={
                      selectedOptionValue == null ||
                      isApplying ||
                      (selectedColumn?.bulkEditStyling === 'status' &&
                        selectedOptionValue === 'closed' &&
                        selectedRows.some(row => row[selectedColumn.key] === selectedOptionValue))
                    }
                    onClick={handleApply}
                    fullWidth
                  />
                </Layout>
              </Layout>
            )}
          </Layout>
        )}
      </Layout>
    </Layout>
  );
};

export default FloatingBulkActions;
